import { AdminApiError, adminApiUrl } from './adminApi';

/**
 * /api/admin/v2/agent-skills — the GLOBAL agent skills (workspace_id NULL), the
 * same rows the product's Settings -> Agent block edits. Superadmin only:
 * the API refuses every other console role. A change applies at once to every
 * workspace on this environment and to MCP clients.
 */
export interface AgentSkillRow {
  id: string;
  slug: string;
  title: string;
  content: string;
  node_type: string | null;
  enabled: boolean;
  date_updated: string;
}

/** A node the picker can offer (non-hidden catalog entries). */
export interface SkillNodeOption {
  type: string;
  name: string;
}

export interface AgentSkillsList {
  skills: AgentSkillRow[];
  nodes: SkillNodeOption[];
}

export interface AgentSkillInput {
  slug: string;
  title: string;
  content: string;
  node_type: string | null;
  enabled: boolean;
}

const BASE = '/api/admin/v2/agent-skills';

async function send<T>(path: string, init: RequestInit, fallback: string): Promise<T> {
  const res = await fetch(adminApiUrl(path), {
    credentials: 'include',
    ...init,
    headers: init.body ? { 'Content-Type': 'application/json' } : undefined,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new AdminApiError(body.error ?? `${fallback}: ${res.status}`, res.status);
  }
  return (await res.json()) as T;
}

export function listSkills(): Promise<AgentSkillsList> {
  return send(BASE, {}, 'skills list failed');
}

export function createSkill(body: AgentSkillInput): Promise<AgentSkillRow> {
  return send(BASE, { method: 'POST', body: JSON.stringify(body) }, 'skill create failed');
}

export function patchSkill(id: string, body: Partial<AgentSkillInput>): Promise<AgentSkillRow> {
  return send(
    `${BASE}/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    'skill update failed',
  );
}

export function deleteSkill(id: string): Promise<{ ok: boolean }> {
  return send(`${BASE}/${encodeURIComponent(id)}`, { method: 'DELETE' }, 'skill delete failed');
}
